import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MessageCircle, FileSearch, Users, Shield, CheckCircle } from 'lucide-react';

const WHATSAPP_NUMBER = "+41779938989";

const processSteps = [
  {
    number: '01',
    icon: MessageCircle,
    title: 'Erstkontakt',
    description: 'Schreiben Sie uns kurz via WhatsApp. Wir melden uns schnell bei Ihnen.',
    details: [
      'Antwort in 24h',
      'Alles vertraulich',
      'Unverbindlich'
    ]
  },
  {
    number: '02',
    icon: FileSearch,
    title: 'Besprechung',
    description: 'Wir sprechen persönlich miteinander und klären genau, was Sie brauchen.',
    details: [
      'Persönliches Gespräch',
      'Genaue Planung',
      'Risiko-Check'
    ]
  },
  {
    number: '03',
    icon: Users,
    title: 'Unser Vorschlag',
    description: 'Sie bekommen ein klares Konzept mit festen Preisen. Keine versteckten Kosten.',
    details: [
      'Passende Lösung',
      'Faire Preise',
      'Anpassbar'
    ]
  },
  {
    number: '04',
    icon: Shield,
    title: 'Umsetzung',
    description: 'Wenn alles passt, legen wir los. Ein fester Ansprechpartner ist immer für Sie da.',
    details: [
      'Schneller Start',
      'Fester Partner',
      'Gute Betreuung'
    ]
  },
];

export const ProcessSection = ({ onOpenWizard }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const openWhatsApp = () => {
    const message = encodeURIComponent("Guten Tag, ich möchte mehr über Ihre Sicherheitsdienstleistungen erfahren.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${message}`, '_blank');
  };

  return (
    <section
      id="prozess"
      className="section-padding bg-[#111625]"
      data-testid="process-section"
    >
      <div className="container-custom">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#D4AF37] uppercase tracking-[0.3em] text-xs font-medium">
            Zusammenarbeit
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F1F5F9] mt-4 mb-6 font-['DM_Sans']">
            In vier Schritten zu Ihrem Schutzkonzept
          </h2>
          <p className="text-[#94A3B8] max-w-2xl mx-auto text-base md:text-lg">
            Von der ersten Kontaktaufnahme bis zum fertigen Konzept – unkompliziert,
            transparent und auf Augenhöhe.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-[#1E293B] -translate-y-1/2" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative"
                  data-testid={`process-step-${index}`}
                >
                  {/* Step Card */}
                  <div className="bg-[#0B0F19] border border-[#1E293B] rounded-sm p-6 h-full hover:border-[#D4AF37]/30 transition-colors group">
                    {/* Step Number */}
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-4xl font-bold text-[#D4AF37]/20 font-['DM_Sans']">
                        {step.number}
                      </span>
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon size={24} className="text-[#0B0F19]" />
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-semibold text-[#F1F5F9] mb-3 font-['DM_Sans']">
                      {step.title}
                    </h3>
                    <p className="text-[#94A3B8] text-sm leading-relaxed mb-4">
                      {step.description}
                    </p>

                    {/* Details */}
                    <ul className="space-y-2">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle size={14} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                          <span className="text-[#64748B]">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Arrow - Mobile/Tablet */}
                  {index < processSteps.length - 1 && (
                    <div className="lg:hidden flex justify-center my-4">
                      <div className="w-px h-8 bg-[#D4AF37]/30" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-[#94A3B8] mb-6">
            Bereit für den ersten Schritt?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={onOpenWizard}
              className="btn-primary"
              data-testid="process-anfrage-btn"
            >
              60-Sekunden Anfrage
            </button>
            <button
              onClick={openWhatsApp}
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="process-whatsapp-btn"
            >
              <MessageCircle size={18} />
              Direkt via WhatsApp
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
